import { Check } from "lucide-react";
import React from "react";

import { plural } from "@/lib/order-check";
import { fmtQty } from "@/lib/scan-ui/status";

/** Строка принятой коробки — для героя успеха. */
export interface AcceptedBoxLine {
  article: string;
  name: string;
  /** Имя характеристики (пусто — без неё): строки коробки бывают одинаковы
   *  всем, кроме характеристики, и без неё неразличимы. */
  characteristic: string;
  partia: string;
  qty: number;
  /** Подпись количества: у сборки «шт», у перемещений — базовая единица
   *  номенклатуры (пусто — не определилась, число без слова). */
  unit: string;
}

/** Принятая целиком сборная коробка (сборка, перемещения) — `decideBox` + запись `…/items/box`. */
export interface AcceptedBox {
  sscc: string;
  lines: AcceptedBoxLine[];
}

/**
 * Герой «коробка принята»: номер, сколько позиций и штук, состав построчно.
 *
 * Один на оба раздела: карточка одиночного скана (степпер, «отменить скан»)
 * про ОДНУ позицию, а здесь их несколько — степпер к коробке неприменим (её
 * состав записан целиком), строки по отдельности правятся в шторке позиций.
 */
export function BoxAcceptedCard({ box }: { box: AcceptedBox }): React.ReactElement {
  const total = box.lines.reduce((sum, l) => sum + l.qty, 0);
  const n = box.lines.length;
  // Итог подписываем, только когда единица у всех строк одна (правило перемещений).
  const units = new Set(box.lines.map((l) => l.unit));
  const totalUnit = units.size === 1 ? [...units][0] : "";
  const withUnit = (q: number, unit: string) => `+${fmtQty(q)}${unit ? ` ${unit}` : ""}`;
  return (
    <div className="oc-flash rounded-xl border-2 border-emerald-300 bg-emerald-50/60 p-3">
      <div className="flex items-center gap-2">
        <span className="text-[18px] font-bold text-emerald-800">Коробка принята</span>
        <Check className="ml-auto size-5 text-emerald-600" />
      </div>
      <div className="mt-1 break-all font-mono text-[13px] text-slate-600">{box.sscc}</div>
      <div className="mt-1 text-[14px] font-bold text-emerald-700">
        {n} {plural(n, ["позиция", "позиции", "позиций"])} · {withUnit(total, totalUnit ?? "")}
      </div>
      <ul className="mt-2 space-y-1">
        {box.lines.map((l, i) => (
          <li key={i} className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[12px]">
            <div className="flex items-center gap-2">
              <span className="font-mono font-bold">{l.article}</span>
              {l.characteristic && <span className="font-semibold text-slate-700">{l.characteristic}</span>}
              <span className="text-slate-500">{l.partia ? `партия ${l.partia}` : "без партии"}</span>
              <span className="ml-auto font-bold text-emerald-700">{withUnit(l.qty, l.unit)}</span>
            </div>
            <div className="line-clamp-1 text-slate-600">{l.name}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}
